import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import {
  getMockVehicleDetails,
  MOCK_ADD_ON_FORM_VALUES,
  MOCK_ADD_ON_TABLES,
  MOCK_ADDITIONAL_EQUIPMENT,
  MOCK_BOX_FORM_VALUES,
  MOCK_CENTRE_OF_GRAVITY_FORM,
  MOCK_CHASSIS_LOADS,
  MOCK_DIAGRAM_PAGES,
  MOCK_LOAD_DISTRIBUTION_FORM_VALUES,
  MOCK_QUOTATION_BOX_STRUCTURE,
  MOCK_QUOTATION_FORM_VALUES,
  MOCK_QUOTATION_LOCKS,
  MOCK_QUOTATION_SAFETY_VEHICLE,
  MOCK_QUOTATION_STANDARD_95,
  MOCK_QUOTATION_TYING_EQUIPMENT,
  MOCK_VEHICLE_FORM_VALUES,
  MOCK_VEHICLE_MEASURES,
  MOCK_WEIGHT_AND_WALL_FORM_VALUES,
} from "../components/WorkOrderDetails/data";
import {
  AddOnDetailsState,
  AddOnFormValues,
  AddOnItemFormValue,
  AddOnTableKey,
  AddOnTableRow,
  AddOnTyingStripModalState,
  AdditionalEquipmentCode,
  AdditionalEquipmentState,
  BoxDetailsFormValues,
  CentreOfGravityAdjustmentMode,
  CentreOfGravityFormValues,
  ChasisLoad,
  ChassisLoadsState,
  VehicleMeasureCode,
  VehicleMeasuresState,
  DiagramDepartmentTab,
  DiagramViewState,
  QuotationBoxStructureState,
  QuotationEquipmentTableKey,
  QuotationLocksState,
  QuotationSafetyVehicleState,
  QuotationStandard95State,
  QuotationTyingEquipmentCardKey,
  QuotationTyingEquipmentState,
  QuotationFrontWallFields,
  QuotationFormValues,
  StatusFilter,
  VehicleDetailsFormValues,
  WeightAndWallFormValues,
  LoadDistributionFormValues,
  WorkOrderDetailTab,
  WorkOrderTableRow,
  WorkOrderVehicleDetails,
} from "../types";
import { initialDiagramView, initialState } from "./initialState";

const clone = <T>(value: T): T => structuredClone(value);

const createInitialTyingStripModal = (): AddOnTyingStripModalState => ({
  open: false,
  height: "",
  note: "",
});

const ensureAddOnDetails = (state: typeof initialState): AddOnDetailsState => {
  if (!state.addOnDetails) {
    state.addOnDetails = {
      form: clone(MOCK_ADD_ON_FORM_VALUES),
      tables: clone(MOCK_ADD_ON_TABLES),
      tyingStripModal: createInitialTyingStripModal(),
    };
  }

  return state.addOnDetails;
};

const createEmptyAddOnTableRow = (): AddOnTableRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  location: "",
  itemType: "",
  quantity: "",
  note: "",
  height: "",
});

export const workOrderSlice = createSlice({
  name: "workOrderState",
  initialState,
  reducers: {
    setStatusFilter: (state, action: PayloadAction<StatusFilter>) => {
      state.statusFilter = action.payload;
    },
    getList: (state) => {
      state.loading = true;
      state.error = null;
    },
    getListSuccess: (state, action: PayloadAction<WorkOrderTableRow[]>) => {
      state.loading = false;
      state.workOrders = action.payload;
    },
    getListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  getWorkOrderDetails: (state, action: PayloadAction<number>) => {
      state.detailLoading = false;
      state.error = null;
      state.selectedWorkOrderId = action.payload;
      state.vehicleDetails = getMockVehicleDetails(action.payload);
      state.loadDistributionForm = clone(MOCK_LOAD_DISTRIBUTION_FORM_VALUES);
      state.diagramView = {
        activeDepartment: DiagramDepartmentTab.DIAGRAM,
        activePage: 0,
        pages: clone(MOCK_DIAGRAM_PAGES),
      };
    },
    getWorkOrderDetailsSuccess: (
      state,
      action: PayloadAction<{
        vehicleDetails: WorkOrderVehicleDetails;
        vehicleDetailsForm: VehicleDetailsFormValues;
        boxDetailsForm: BoxDetailsFormValues;
        addOnDetails: AddOnDetailsState;
        diagramView: DiagramViewState;
      }>
    ) => {
      state.detailLoading = false;
      state.vehicleDetails = action.payload.vehicleDetails;
      state.vehicleDetailsForm = action.payload.vehicleDetailsForm;
      state.boxDetailsForm = action.payload.boxDetailsForm;
      state.addOnDetails = action.payload.addOnDetails;
      state.diagramView = action.payload.diagramView;
    },
    getWorkOrderDetailsFailure: (state, action: PayloadAction<string>) => {
      state.detailLoading = false;
      state.error = action.payload;
    },
    clearWorkOrderDetails: (state) => {
      state.selectedWorkOrderId = null;
      state.vehicleDetails = null;
      state.vehicleDetailsForm = null;
      state.boxDetailsForm = null;
      state.weightAndWallForm = null;
      state.loadDistributionForm = null;
      state.addOnDetails = null;
      state.quotationForm = null;
      state.quotationBoxStructure = null;
      state.quotationTyingEquipment = null;
      state.quotationSafetyVehicle = null;
      state.quotationLocks = null;
      state.quotationStandard95 = null;
      state.centreOfGravityForm = null;
      state.chassisLoads = null;
      state.vehicleMeasures = null;
      state.additionalEquipment = null;
      state.diagramView = initialDiagramView;
      state.activeDetailTab = WorkOrderDetailTab.VEHICLE_DETAILS;
      state.detailLoading = false;
    },
    setActiveDetailTab: (state, action: PayloadAction<WorkOrderDetailTab>) => {
      state.activeDetailTab = action.payload;
    },
    updateVehicleDetailsFormField: (
      state,
      action: PayloadAction<{
        field: keyof VehicleDetailsFormValues;
        value: string | boolean;
      }>
    ) => {
      if (!state.vehicleDetailsForm) {
        state.vehicleDetailsForm = clone(MOCK_VEHICLE_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.vehicleDetailsForm[field] = value as never;
    },
    updateBoxDetailsFormField: (
      state,
      action: PayloadAction<{
        field: keyof BoxDetailsFormValues;
        value: string | boolean;
      }>
    ) => {
      if (!state.boxDetailsForm) {
        state.boxDetailsForm = clone(MOCK_BOX_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.boxDetailsForm[field] = value as never;
    },
    updateWeightAndWallFormField: (
      state,
      action: PayloadAction<{
        field: keyof WeightAndWallFormValues;
        value: string;
      }>
    ) => {
      if (!state.weightAndWallForm) {
        state.weightAndWallForm = clone(MOCK_WEIGHT_AND_WALL_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.weightAndWallForm[field] = value;
    },
    updateLoadDistributionFormField: (
      state,
      action: PayloadAction<{
        field: keyof LoadDistributionFormValues;
        value: string;
      }>
    ) => {
      if (!state.loadDistributionForm) {
        state.loadDistributionForm = clone(MOCK_LOAD_DISTRIBUTION_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.loadDistributionForm[field] = value;
    },
    updateQuotationFormField: (
      state,
      action: PayloadAction<{
        field: keyof QuotationFormValues;
        value: string | boolean;
      }>
    ) => {
      if (!state.quotationForm) {
        state.quotationForm = clone(MOCK_QUOTATION_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.quotationForm[field] = value as never;
    },
    updateQuotationBoxStructure: (
      state,
      action: PayloadAction<{
        key: keyof QuotationBoxStructureState;
        value: Partial<AddOnItemFormValue> | Partial<QuotationFrontWallFields>;
      }>
    ) => {
      if (!state.quotationBoxStructure) {
        state.quotationBoxStructure = clone(MOCK_QUOTATION_BOX_STRUCTURE);
      }

      const { key, value } = action.payload;
      if (key === "frontWall") {
        state.quotationBoxStructure.frontWall = {
          ...state.quotationBoxStructure.frontWall,
          ...(value as Partial<QuotationFrontWallFields>),
        };
        return;
      }

      state.quotationBoxStructure[key] = {
        ...state.quotationBoxStructure[key],
        ...(value as Partial<AddOnItemFormValue>),
      };
    },
    updateQuotationTyingEquipment: (
      state,
      action: PayloadAction<{
        key: QuotationTyingEquipmentCardKey;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationTyingEquipment) {
        state.quotationTyingEquipment = clone(MOCK_QUOTATION_TYING_EQUIPMENT);
      }

      const { key, value } = action.payload;
      state.quotationTyingEquipment[key] = {
        ...state.quotationTyingEquipment[key],
        ...value,
      };
    },
    addQuotationEquipmentTableRow: (
      state,
      action: PayloadAction<QuotationEquipmentTableKey>
    ) => {
      if (!state.quotationTyingEquipment) {
        state.quotationTyingEquipment = clone(MOCK_QUOTATION_TYING_EQUIPMENT);
      }

      state.quotationTyingEquipment[action.payload].push(createEmptyAddOnTableRow());
    },
    updateQuotationSafetyVehicle: (
      state,
      action: PayloadAction<{
        key: keyof QuotationSafetyVehicleState;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationSafetyVehicle) {
        state.quotationSafetyVehicle = clone(MOCK_QUOTATION_SAFETY_VEHICLE);
      }

      const { key, value } = action.payload;
      state.quotationSafetyVehicle[key] = {
        ...state.quotationSafetyVehicle[key],
        ...value,
      };
    },
    updateQuotationLocks: (
      state,
      action: PayloadAction<{
        key: keyof QuotationLocksState;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationLocks) {
        state.quotationLocks = clone(MOCK_QUOTATION_LOCKS);
      }

      const { key, value } = action.payload;
      state.quotationLocks[key] = {
        ...state.quotationLocks[key],
        ...value,
      };
    },
    updateQuotationStandard95: (
      state,
      action: PayloadAction<{
        key: keyof QuotationStandard95State;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationStandard95) {
        state.quotationStandard95 = clone(MOCK_QUOTATION_STANDARD_95);
      }

      const { key, value } = action.payload;
      state.quotationStandard95[key] = {
        ...state.quotationStandard95[key],
        ...value,
      };
    },
    resetQuotationStandard95: (state) => {
      state.quotationStandard95 = clone(MOCK_QUOTATION_STANDARD_95);
    },
    calculateQuotationPrice: (state) => {
      // Placeholder until quotation pricing API is available
      state.error = null;
    },
    updateCentreOfGravityFormField: (
      state,
      action: PayloadAction<{
        field: keyof CentreOfGravityFormValues;
        value: string;
      }>
    ) => {
      if (!state.centreOfGravityForm) {
        state.centreOfGravityForm = clone(MOCK_CENTRE_OF_GRAVITY_FORM);
      }

      const { field, value } = action.payload;
      state.centreOfGravityForm[field] = value as never;
    },
    setCentreOfGravityAdjustmentMode: (
      state,
      action: PayloadAction<CentreOfGravityAdjustmentMode>
    ) => {
      if (!state.centreOfGravityForm) {
        state.centreOfGravityForm = clone(MOCK_CENTRE_OF_GRAVITY_FORM);
      }

      state.centreOfGravityForm.adjustmentMode = action.payload;
    },
    resetCentreOfGravityForm: (state) => {
      state.centreOfGravityForm = clone(MOCK_CENTRE_OF_GRAVITY_FORM);
    },
    updateChassisLoadValue: (
      state,
      action: PayloadAction<{ key: ChasisLoad; value: string }>
    ) => {
      if (!state.chassisLoads) {
        state.chassisLoads = clone(MOCK_CHASSIS_LOADS);
      }

      const { key, value } = action.payload;
      state.chassisLoads[key] = {
        ...state.chassisLoads[key],
        value,
      };
    },
    updateVehicleMeasureValue: (
      state,
      action: PayloadAction<{ key: VehicleMeasureCode; value: string }>
    ) => {
      if (!state.vehicleMeasures) {
        state.vehicleMeasures = clone(MOCK_VEHICLE_MEASURES);
      }

      const { key, value } = action.payload;
      state.vehicleMeasures[key] = {
        ...state.vehicleMeasures[key],
        value,
      };
    },
    updateVehicleMeasureCalculationValue: (
      state,
      action: PayloadAction<{ key: VehicleMeasureCode; calculationValue: string }>
    ) => {
      if (!state.vehicleMeasures) {
        state.vehicleMeasures = clone(MOCK_VEHICLE_MEASURES);
      }

      const { key, calculationValue } = action.payload;
      state.vehicleMeasures[key] = {
        ...state.vehicleMeasures[key],
        calculationValue,
      };
    },
    updateAdditionalEquipmentValue: (
      state,
      action: PayloadAction<{ key: AdditionalEquipmentCode; value: string }>
    ) => {
      if (!state.additionalEquipment) {
        state.additionalEquipment = clone(MOCK_ADDITIONAL_EQUIPMENT);
      }

      const { key, value } = action.payload;
      state.additionalEquipment[key] = {
        ...state.additionalEquipment[key],
        value,
      };
    },
    updateAddOnFormField: (
      state,
      action: PayloadAction<{
        field: keyof AddOnFormValues;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { field, value } = action.payload;
      addOnDetails.form[field] = {
        ...addOnDetails.form[field],
        ...value,
      };
    },
    addAddOnTableRow: (state, action: PayloadAction<AddOnTableKey>) => {
      const addOnDetails = ensureAddOnDetails(state);

      addOnDetails.tables[action.payload].push(createEmptyAddOnTableRow());
    },
    addAddOnTableRowWithValues: (
      state,
      action: PayloadAction<{
        tableKey: AddOnTableKey;
        values: Partial<Omit<AddOnTableRow, "id">>;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { tableKey, values } = action.payload;
      addOnDetails.tables[tableKey].push({
        ...createEmptyAddOnTableRow(),
        ...values,
      });
    },
    updateAddOnTableRow: (
      state,
      action: PayloadAction<{
        tableKey: AddOnTableKey;
        rowId: string;
        field: keyof Omit<AddOnTableRow, "id">;
        value: string;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { tableKey, rowId, field, value } = action.payload;
      const row = addOnDetails.tables[tableKey].find((item) => item.id === rowId);

      if (!row) {
        return;
      }

      row[field] = value;
    },
    openAddOnTyingStripModal: (state) => {
      const addOnDetails = ensureAddOnDetails(state);

      addOnDetails.tyingStripModal = {
        open: true,
        height: "",
        note: "",
      };
    },
    closeAddOnTyingStripModal: (state) => {
      const addOnDetails = ensureAddOnDetails(state);

      addOnDetails.tyingStripModal = createInitialTyingStripModal();
    },
    updateAddOnTyingStripModalField: (
      state,
      action: PayloadAction<{
        field: keyof Omit<AddOnTyingStripModalState, "open">;
        value: string;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { field, value } = action.payload;
      addOnDetails.tyingStripModal[field] = value;
    },
    submitAddOnTyingStripModal: (state) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { height, note } = addOnDetails.tyingStripModal;
      addOnDetails.tables.tyingStrip.push({
        ...createEmptyAddOnTableRow(),
        height,
        note,
      });
      addOnDetails.tyingStripModal = createInitialTyingStripModal();
    },
    setDiagramDepartment: (state, action: PayloadAction<DiagramDepartmentTab>) => {
      state.diagramView.activeDepartment = action.payload;
    },
    setDiagramPage: (state, action: PayloadAction<number>) => {
      state.diagramView.activePage = action.payload;
    },
    exportDiagramPdf: (state) => {
      // Placeholder until PDF export API is available
      state.error = null;
    },
  },
});

export const { reducer, actions, name: sliceKey } = workOrderSlice;
